import type { JSX } from 'react';
import { Link } from 'react-router';
import { UNSPECIFIED_COUNTY } from '../../../src/wire';
import { ChronicleRail } from '../components/ChronicleRail';
import { InfoPopover } from '../components/InfoPopover';
import { LiveCounter, LiveCounterGroup } from '../components/LiveCounter';
import { HourSparkbar, LeaderboardList, MedianCards, RecordPlate } from '../components/Tabloo';
import { Skeleton } from '../components/ui/skeleton';
import {
  type CounterAsOf,
  type CoverageFact,
  corpusClaim,
  counterClaim,
  frozenCounterNote,
  sourceCoverage,
  sourceMeta,
} from '../coverage';
import { damageDenominator, formatCount, formatDate } from '../format';
import {
  CATEGORY_LABELS,
  categoryPath,
  excludedDamageNote,
  NOT_AN_INCIDENT,
  navCategoryLabel,
} from '../labels';
import {
  last30Days,
  monthToDate,
  useBiggestDamage,
  useDigestCounter,
  useTrafficCounter,
  useTrafficFacetsFor,
  ytdWindow,
} from '../useLanding';
import { type CounterState, useLiveCounter } from '../useLiveCounter';
import { useMeta } from '../useStats';

/**
 * The front door: three ticking year-to-date figures, and what they are counted out of. Every
 * number is an estimate the reader is told is an estimate — the counters are anchored to the day
 * each series was last read, the shared caption names those days, and a counter whose baseline is
 * not a whole year-to-date total stops rather than sprinting over the gap.
 */

/** One placeholder chip per category the taxonomy carries, so the grid opens at its settled size. */
const RUBRIIGID_SLOTS = Object.keys(CATEGORY_LABELS).filter((c) => c !== NOT_AN_INCIDENT);

/** The frozen sentence, or nothing. It is a second line on the tile rather than a replacement for
 * the note: a euro total may not lose its denominator because the corpus stopped moving. */
function heroStatus(state: CounterState, pending: number, coverage: CoverageFact): string | null {
  return state.kind === 'frozen'
    ? frozenCounterNote(state.asOf, state.reason, pending, coverage)
    : null;
}

export default function Landing(): JSX.Element {
  const { data: meta, isPending: metaPending } = useMeta();

  const digest = sourceMeta(meta, 'digest');
  const digestCoverage = sourceCoverage(meta, 'digest');
  const trafficCoverage = sourceCoverage(meta, 'traffic');

  const fraudFeed = useDigestCounter('FRAUD', 'damage_cents');
  const theftFeed = useDigestCounter('THEFT', 'damage_cents');
  const trafficFeed = useTrafficCounter();

  const fraud = useLiveCounter({ ...fraudFeed.input, coverage: digestCoverage }, 'euro');
  const theft = useLiveCounter({ ...theftFeed.input, coverage: digestCoverage }, 'euro');
  const traffic = useLiveCounter({ ...trafficFeed.input, coverage: trafficCoverage }, 'count');

  const digestPending = digest?.pending_extractions ?? meta?.pending_extractions ?? 0;
  const trafficPending = sourceMeta(meta, 'traffic')?.pending_extractions ?? 0;

  const anchors: CounterAsOf[] = [
    { label: 'kelmuste', state: fraud.state },
    { label: 'varguste', state: theft.state },
    { label: 'liikluse', state: traffic.state },
  ].flatMap(({ label, state }) =>
    state.kind === 'live' || state.kind === 'frozen' ? [{ label, asOf: state.asOf }] : [],
  );
  const claim = counterClaim(anchors);

  const traffic30 = last30Days();
  const facets30 = useTrafficFacetsFor(traffic30);
  const monthRecord = useBiggestDamage(monthToDate());
  const yearRecord = useBiggestDamage(ytdWindow());

  // The hour histogram is asked over the traffic series' whole read span, not over 30 days: a
  // 24-bar shape built from one month of accidents is noise with a label on its loudest bar.
  const trafficMeta = sourceMeta(meta, 'traffic');
  const corpusSpan =
    trafficMeta?.date_min == null || trafficMeta.date_max == null
      ? null
      : { from: trafficMeta.date_min, to: trafficMeta.date_max };
  const facetsAll = useTrafficFacetsFor(corpusSpan ?? traffic30);
  const corpusHours = corpusSpan === null ? [] : (facetsAll.data?.hours ?? []);
  const hoursCaption =
    corpusSpan === null
      ? 'Liiklusõnnetused kellaajati'
      : `Liiklusõnnetused kellaajati, ${formatDate(corpusSpan.from)} – ${formatDate(corpusSpan.to)}`;

  // The county facet's own denominator: how many of the window's accidents named a county at all.
  const statedCounties = (facets30.data?.counties ?? [])
    .filter((c) => c.key !== UNSPECIFIED_COUNTY)
    .reduce((sum, c) => sum + c.count, 0);

  const note = {
    fraud: `${formatCount(fraudFeed.stats?.incident_count ?? 0)} kelmust · ${damageDenominator(fraudFeed.stats?.damage_incident_count ?? 0)}`,
    theft: `${formatCount(theftFeed.stats?.incident_count ?? 0)} vargust · ${damageDenominator(theftFeed.stats?.damage_incident_count ?? 0)}`,
    // Vehicles and people the reports described — never a disposition or an outcome, both of which
    // need a sentence of their own that a counter caption has no room for.
    traffic: `${formatCount(trafficFeed.stats?.vehicles_involved ?? 0)} sõidukit · ${formatCount(trafficFeed.stats?.people_described ?? 0)} inimest kirjeldatud`,
  };

  const categories = (digest?.categories ?? meta?.categories ?? []).filter(
    (c) => c.category !== NOT_AN_INCIDENT,
  );
  const lede = corpusClaim(
    categories.reduce((sum, c) => sum + c.count, 0),
    digestCoverage,
  );

  // `null` is "no figure": a skeleton while loading, an em dash when the window holds nothing.
  const value = (counter: { state: CounterState; shown: number }): number | null =>
    counter.state.kind === 'pending' || counter.state.kind === 'empty' ? null : counter.shown;

  return (
    /*
     * The landing grid (ui-direction §3): the page from `lg` up, the chronicle beside it at 21rem.
     *
     * Below `lg` the wrapper is `display: contents`, so its three sections are grid items in their
     * own right and `order` can slot the rail's teaser card in right after the hero — the block
     * order the mockup asks for (hero, kroonika, Tabloo, Rubriigid) without moving the rail out of
     * column 2. From `lg` the wrapper is a block again, `order` stops applying, and the DOM order
     * stands: page first, rail second.
     */
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_21rem] lg:items-start">
      <div className="contents lg:block">
        <section aria-label="Tabloo">
          <p className="mb-2 inline-block rounded-sm bg-accent-wash px-2.5 py-1 text-caption font-semibold tracking-[0.08em] text-accent-deep uppercase">
            Tabloo · politsei pressiteadetest tänavu
          </p>

          {/* The tiles keep their settled height from the first paint: the notes under each figure
              arrive with the data and used to push every section below them down. Three values
              because the tiles stack below `md`, and the stack is more than twice as tall. */}
          <LiveCounterGroup
            claim={claim}
            className="grid min-h-[34rem] gap-3 sm:min-h-[32rem] md:min-h-[16rem] md:grid-cols-[1.35fr_1fr_1fr]"
          >
            <LiveCounter
              lead
              flavour="euro"
              label="Kelmustega välja petetud tänavu"
              href="/kelmused"
              value={value(fraud)}
              pending={fraud.state.kind === 'pending'}
              frozen={fraud.state.kind !== 'live'}
              animate={fraud.animates}
              note={note.fraud}
              status={heroStatus(fraud.state, digestPending, digestCoverage)}
              excluded={excludedDamageNote(
                fraudFeed.stats?.damage_flagged_cents ?? 0,
                fraudFeed.stats?.damage_flagged_count ?? 0,
              )}
            />
            <LiveCounter
              flavour="count"
              label="Liiklusõnnetusi tänavu"
              href="/liiklusonnetused"
              value={value(traffic)}
              pending={traffic.state.kind === 'pending'}
              frozen={traffic.state.kind !== 'live'}
              animate={traffic.animates}
              note={note.traffic}
              status={heroStatus(traffic.state, trafficPending, trafficCoverage)}
            />
            <LiveCounter
              flavour="euro"
              label="Vargustega tekitatud kahju tänavu"
              href="/vargused"
              value={value(theft)}
              pending={theft.state.kind === 'pending'}
              frozen={theft.state.kind !== 'live'}
              animate={theft.animates}
              note={note.theft}
              status={heroStatus(theft.state, digestPending, digestCoverage)}
              excluded={excludedDamageNote(
                theftFeed.stats?.damage_flagged_cents ?? 0,
                theftFeed.stats?.damage_flagged_count ?? 0,
              )}
            />
          </LiveCounterGroup>

          {/* The slot is held open whether or not the claim has arrived. */}
          <div className="mt-1 mb-8 min-h-10 max-w-[46rem] text-small text-ink-secondary sm:min-h-5">
            {lede !== null && (
              <p className="m-0">
                {lede.short}
                <InfoPopover label="Katvuse täpne selgitus">{lede.full}</InfoPopover>
              </p>
            )}
          </div>
        </section>

        <section aria-label="Tabloo moodulid" className="order-2 mb-8 lg:order-none">
          <div className="grid gap-3.5 sm:grid-cols-2">
            <LeaderboardList
              counties={facets30.data?.counties ?? []}
              stated={statedCounties}
              total={facets30.data?.incidents_total ?? 0}
              pending={facets30.isPending}
            />
            <RecordPlate
              incident={monthRecord.data ?? null}
              yearBest={yearRecord.data ?? null}
              pending={monthRecord.isPending}
            />
            <MedianCards
              theft={theftFeed.stats?.damage_cents_median ?? null}
              fraud={fraudFeed.stats?.damage_cents_median ?? null}
            />
            <HourSparkbar hours={corpusHours} caption={hoursCaption} />
          </div>
        </section>

        <section aria-label="Rubriigid" className="order-3 lg:order-none">
          <h2 className="font-display text-h2 font-bold tracking-[-0.01em] text-accent-deep">
            Rubriigid
          </h2>
          <p className="mt-0.5 mb-3 text-small text-ink-muted">
            Kõik kategooriad juhtumite arvu järgi
          </p>
          <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(12rem,1fr))] gap-2 p-0">
            {metaPending &&
              RUBRIIGID_SLOTS.map((slot) => (
                <li key={slot} aria-hidden="true">
                  <div className="flex items-baseline justify-between gap-2 rounded-sm border border-line bg-surface px-3 py-2 text-[0.85rem]">
                    {/* The label is drawn invisibly under the placeholder so that a name long
                        enough to wrap reserves both of its lines at every column width. */}
                    <Skeleton className="min-w-0 font-semibold break-words text-transparent">
                      {navCategoryLabel(slot)}
                    </Skeleton>
                    <Skeleton className="tabular-nums whitespace-nowrap text-transparent">
                      0000
                    </Skeleton>
                  </div>
                </li>
              ))}
            {categories.map((c) => (
              <li key={c.category}>
                <Link
                  to={categoryPath(c.category)}
                  className="flex items-baseline justify-between gap-2 rounded-sm border border-line bg-surface px-3 py-2 text-[0.85rem] text-ink no-underline transition-colors hover:border-accent"
                >
                  <span className="min-w-0 font-semibold break-words">
                    {navCategoryLabel(c.category)}
                  </span>
                  <span className="tabular-nums whitespace-nowrap text-ink-muted">
                    {formatCount(c.count)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="order-1 lg:order-none">
        <ChronicleRail />
      </div>
    </div>
  );
}
